import { ethers } from 'ethers';
import type { Eip1193Provider } from 'ethers';

// Named import so the ~98KB of contract bytecode in the Hardhat artifact is
// tree-shaken out -- the browser only ever needs the ABI.
import { abi as DynaStripesAbi } from '../artifacts/contracts/DynaStripes.sol/DynaStripes.json';
import * as Errors from './ErrorMessages';
import DynaStripesContractAddress, { DynaStripesCurrentNetworkID, DynaStripesCurrentNetworkName, DynaStripesCurrentNetworkCurrencySymbol, DynaStripesCurrentNetworkRpcUrl, DynaStripesCurrentNetworkExplorerUrl, DynaStripesReadRpcUrls } from './Constants';
import { AccountDetails } from './AccountDetails';
import { fetchCachedAccountDetails, setCachedAccountDetails } from './LocalStorage';
import { resolveProvider, getSelectedWalletRdns } from './WalletProviders';

let cachedReadProvider: ethers.AbstractProvider | null = null;

/**
 * Read-only provider over public RPC endpoints.
 *
 * Public data -- mint price, token counts, the gallery, token metadata -- is
 * readable by anyone, so it must not depend on a wallet being installed,
 * connected, or on the right network. Several endpoints are used behind a
 * FallbackProvider so one going dark doesn't take the site with it (the
 * original maticvigil endpoint has since been shut down).
 */
export function getReadProvider(): ethers.AbstractProvider {
  if (cachedReadProvider) {
    return cachedReadProvider;
  }

  const network = new ethers.Network(DynaStripesCurrentNetworkName, DynaStripesCurrentNetworkID);
  const providers = DynaStripesReadRpcUrls.map(
    (url: string) => new ethers.JsonRpcProvider(url, network, { staticNetwork: network }),
  );

  cachedReadProvider = providers.length === 1
    ? providers[0]
    : new ethers.FallbackProvider(providers.map((provider, index) => ({
        provider,
        priority: index + 1,
        stallTimeout: 2000,
      })), network, { quorum: 1 });

  return cachedReadProvider;
}

let cachedBrowserProvider: ethers.BrowserProvider | null = null;
let cachedForWallet: string | null = null;
let cachedEip1193Provider: Eip1193Provider | null = null;

/** Drop the memoised provider, e.g. after the user switches wallet. */
export function resetProvider() {
  console.log('[BlockchainAPI] Resetting cached provider');
  pendingAccountDetails = null;
  cachedBrowserProvider = null;
  cachedForWallet = null;
  cachedEip1193Provider = null;
}

export async function getProvider(): Promise<ethers.BrowserProvider> {
  const selectedWallet = getSelectedWalletRdns();

  if (cachedBrowserProvider && cachedForWallet === selectedWallet) {
    return cachedBrowserProvider;
  }

  console.log('[BlockchainAPI] Creating BrowserProvider...');
  const injected = await resolveProvider();

  cachedEip1193Provider = injected;
  cachedForWallet = getSelectedWalletRdns();
  cachedBrowserProvider = new ethers.BrowserProvider(injected);

  return cachedBrowserProvider;
}

/**
 * The raw EIP-1193 provider behind {@link getProvider}, for `.on()`/`.removeListener()`.
 * Returns null before a provider has been resolved.
 */
export function getInjectedProvider(): Eip1193Provider | null {
  return cachedEip1193Provider;
}

export async function isOnCorrectNetwork(): Promise<boolean> {
  const provider = await getProvider();
  const network = await provider.getNetwork();
  console.log("[BlockchainAPI] Network: " + network.chainId.toString());
  return network.chainId === BigInt(DynaStripesCurrentNetworkID);
}

export async function switchToCurrentNetwork(): Promise<void> {
  // Attempts to add the current network; MetaMask switches to it if already present.
  console.log("[BlockchainAPI] Switching to " + DynaStripesCurrentNetworkName + "...");

  const provider = await getProvider();

  const chainIdHex = "0x" + DynaStripesCurrentNetworkID.toString(16);

  try {
    await provider.send("wallet_switchEthereumChain", [{ chainId: chainIdHex }]);
  } catch (switchError: any) {
    // 4902 indicates the chain has not been added to the wallet yet.
    if (switchError.code === 4902 || switchError.error?.code === 4902 || String(switchError).includes('Unrecognized chain ID')) {
      const data = [{
        chainId: chainIdHex,
        chainName: DynaStripesCurrentNetworkName,
        nativeCurrency:
        {
          name: DynaStripesCurrentNetworkCurrencySymbol,
          symbol: DynaStripesCurrentNetworkCurrencySymbol,
          decimals: 18
        },
        rpcUrls: [DynaStripesCurrentNetworkRpcUrl],
        blockExplorerUrls: [DynaStripesCurrentNetworkExplorerUrl],
      }];

      try {
        await provider.send("wallet_addEthereumChain", data);
      } catch (addError) {
        console.error("[BlockchainAPI] Failed to add chain:", addError);
      }
    } else {
      console.error("[BlockchainAPI] Failed to switch chain:", switchError);
    }
  }
}

/** Contract for reading public data. Needs no wallet. */
export async function getContract(): Promise<ethers.Contract> {
  return new ethers.Contract(DynaStripesContractAddress, DynaStripesAbi, getReadProvider());
}

export async function getSigner(): Promise<ethers.JsonRpcSigner> {
  const provider = await getProvider();
  return provider.getSigner();
}

export async function getContractWithSigner(): Promise<ethers.Contract> {
  const provider = await getProvider();

  const { chainId } = await provider.getNetwork();
  if (chainId !== BigInt(DynaStripesCurrentNetworkID)) {
    console.log("[BlockchainAPI] Not on right network");
    throw Error(Errors.DS_WRONG_ETH_NETWORK);
  }

  const signer = await provider.getSigner();
  const contract = new ethers.Contract(DynaStripesContractAddress, DynaStripesAbi, signer);
  return contract;
}

/** Passive check -- never prompts the wallet. */
export async function isAccountConnected(): Promise<boolean> {
  try {
    const provider = await getProvider();
    const accounts = await provider.listAccounts();
    return accounts.length > 0;
  } catch (error) {
    console.log("[BlockchainAPI] isAccountConnected, error: " + error);
    return false;
  }
}

/**
 * Address of the connected account, prompting for authorisation if needed.
 * Goes through the ethers provider rather than `window.ethereum` so it uses
 * whichever wallet the user selected.
 */
async function fetchAccountAddress(provider: ethers.BrowserProvider, promptIfNeeded: boolean): Promise<string> {
  const accounts = await provider.listAccounts();
  console.log("[BlockchainAPI] Accounts listed, count:", accounts.length);

  if (accounts.length > 0) {
    return accounts[0].address;
  }

  if (!promptIfNeeded) {
    throw Error(Errors.DS_NO_ETH_ACCOUNT);
  }

  console.log("[BlockchainAPI] No authorised accounts, requesting access...");
  const requested: string[] = await provider.send('eth_requestAccounts', []);

  if (!requested || requested.length === 0) {
    throw Error(Errors.DS_NO_ETH_ACCOUNT);
  }

  console.log("[BlockchainAPI] Access granted for:", requested[0]);
  return ethers.getAddress(requested[0]);
}

/**
 * In-flight connect request. Both the nav dropdown and the mint button can ask
 * to connect, and a user can click twice -- without this they each fire their
 * own eth_requestAccounts and the wallet rejects the extras with -32002
 * ("already pending").
 */
let pendingAccountDetails: Promise<AccountDetails> | null = null;

async function loadAccountDetails(promptIfNeeded: boolean): Promise<AccountDetails> {
  const provider = await getProvider();
  const fullAddress = await fetchAccountAddress(provider, promptIfNeeded);

  let shortenedAddress = fullAddress;
  if (shortenedAddress.length > 10) {
    shortenedAddress = shortenedAddress.substring(0, 6) + "..." + shortenedAddress.slice(-4);
  }

  const weiBalance = await provider.getBalance(fullAddress);
  const displayBalance = Number(ethers.formatEther(weiBalance)).toFixed(4);

  const accountDetails = new AccountDetails(shortenedAddress, fullAddress, weiBalance.toString(), displayBalance);

  setCachedAccountDetails(accountDetails);

  return accountDetails;
}

/**
 * The wallet's currently selected account, or null if none is authorised.
 * Resolving the provider as a side effect also means event listeners can be
 * attached after a session is restored from cache.
 */
export async function fetchConnectedAddress(): Promise<string | null> {
  try {
    const provider = await getProvider();
    const accounts = await provider.listAccounts();
    return accounts.length > 0 ? accounts[0].address : null;
  } catch (error: any) {
    console.log("[BlockchainAPI] Could not read connected address: " + error?.message);
    return null;
  }
}

/**
 * Ask the wallet to re-prompt for which account(s) this site may see.
 *
 * Switching account inside MetaMask does nothing for a site that was never
 * granted permission for that account -- eth_accounts simply keeps returning
 * the previously permitted ones. This opens the wallet's account picker so the
 * user can actually hand over the account they switched to.
 */
export async function requestAccountSelection(): Promise<void> {
  const provider = await getProvider();
  await provider.send('wallet_requestPermissions', [{ eth_accounts: {} }]);
  pendingAccountDetails = null;
}

export async function fetchAccountDetails(promptIfNeeded = true): Promise<AccountDetails> {
  console.log("[BlockchainAPI] Fetching account details..");

  const cachedAccountDetails = fetchCachedAccountDetails();
  if (cachedAccountDetails !== null) {
    return cachedAccountDetails;
  }

  if (pendingAccountDetails) {
    console.log("[BlockchainAPI] Reusing in-flight account request");
    return pendingAccountDetails;
  }

  pendingAccountDetails = loadAccountDetails(promptIfNeeded)
    .finally(() => { pendingAccountDetails = null; });

  return pendingAccountDetails;
}

export async function isCurrentAccountOwner(): Promise<boolean> {
  const connected = await isAccountConnected();
  if (!connected) {
    console.log("[BlockchainAPI] Not connected.");
    return false;
  }

  const provider = await getProvider();
  const accountAddress = await fetchAccountAddress(provider, false);

  const ethAddress = accountAddress.toLowerCase();
  const contract = await getContract();
  const ownerAddress = (await contract.owner()).toLowerCase();

  return ethAddress === ownerAddress;
}

export async function fetchMintPrice(): Promise<bigint> {
  const contract = await getContract();
  const mintPrice = await contract.getMintPrice();
  console.log("[BlockchainAPI] Mint price: " + mintPrice.toString());
  return mintPrice;
}

export async function fetchFormattedMintPrice(): Promise<string> {
  const mintPrice = await fetchMintPrice();
  return ethers.formatEther(mintPrice);
}

export type MintPreflight =
  | { reason: 'ok' }
  | { reason: 'revert'; detail: string }
  | { reason: 'insufficient-funds'; required: string; balance: string };

/**
 * Dry-run a mint against a public RPC before asking the wallet to sign.
 *
 * Wallets differ wildly in what they tell you: some simulate and surface the
 * revert reason, others (Phantom) fail to simulate at all and return a bare
 * -32603 "Unexpected error". Checking it ourselves against a provider we
 * control means we always know whether a mint is actually valid -- so a doomed
 * one is stopped here with a real reason instead of costing gas, and a valid
 * one can be reported as valid even when the wallet is unsure.
 *
 * Two separate things can go wrong, and eth_call only catches the first:
 *   1. the contract would revert (bad parameters, sold out, paused)
 *   2. the account can't cover gas -- eth_call ignores balance entirely, so
 *      this has to be checked by hand or the wallet is the first to notice
 *
 * Network trouble reaching the RPC reports ok -- a flaky endpoint must never
 * block a legitimate mint.
 */
export async function preflightMint(account: string, args: unknown[], value: bigint): Promise<MintPreflight> {
  const contract = await getContract();

  try {
    await contract.mintStripes.staticCall(...args, { from: account, value });
  } catch (error: any) {
    if (error?.code === 'CALL_EXCEPTION') {
      const detail = error.reason ?? error.shortMessage ?? 'the contract rejected it';
      console.log("[BlockchainAPI] Mint dry-run reverted: " + detail);
      return { reason: 'revert', detail };
    }
    console.warn("[BlockchainAPI] Mint dry-run inconclusive, continuing:", error?.shortMessage ?? error?.message);
    return { reason: 'ok' };
  }

  // The call itself is fine; can the account pay for it?
  try {
    const provider = getReadProvider();
    const [gas, feeData, balance] = await Promise.all([
      contract.mintStripes.estimateGas(...args, { from: account, value }),
      provider.getFeeData(),
      provider.getBalance(account),
    ]);

    const gasPrice = feeData.maxFeePerGas ?? feeData.gasPrice;
    if (gasPrice == null) {
      return { reason: 'ok' };
    }

    const required = gas * gasPrice + value;
    console.log("[BlockchainAPI] Mint needs " + required + " wei, balance is " + balance);

    if (balance < required) {
      return {
        reason: 'insufficient-funds',
        required: Number(ethers.formatEther(required)).toFixed(4),
        balance: Number(ethers.formatEther(balance)).toFixed(4),
      };
    }
  } catch (error: any) {
    console.warn("[BlockchainAPI] Could not check gas affordability:", error?.shortMessage ?? error?.message);
  }

  console.log("[BlockchainAPI] Mint dry-run succeeded");
  return { reason: 'ok' };
}

export interface CollectionStatus {
  mintPrice: bigint;
  formattedMintPrice: string;
  tokenCount: string;
  /** The immutable hard cap, not the owner-adjustable getTokenLimit(). */
  tokenLimit: string;
}

/**
 * Everything the mint page needs about the collection, in one round trip.
 * Previously the price was fetched twice and the counts separately.
 */
export async function fetchCollectionStatus(): Promise<CollectionStatus> {
  const contract = await getContract();

  const [mintPrice, tokenCount, tokenLimit] = await Promise.all([
    contract.getMintPrice(),
    contract.totalSupply(),
    contract.TOKEN_LIMIT(),
  ]);

  console.log("[BlockchainAPI] Collection status: price " + mintPrice + ", " + tokenCount + "/" + tokenLimit);

  return {
    mintPrice,
    formattedMintPrice: ethers.formatEther(mintPrice),
    tokenCount: tokenCount.toString(),
    tokenLimit: tokenLimit.toString(),
  };
}

/**
 * Token IDs for the most recently minted tokens, newest first.
 *
 * The index lookups are issued together rather than awaited one at a time:
 * ethers batches same-tick calls into a single JSON-RPC request, so twelve
 * lookups cost one round trip instead of twelve.
 */
export async function fetchRecentTokenIds(maxToDisplay = 12): Promise<string[]> {
  const contract = await getContract();

  const tokenCount = Number(await contract.totalSupply());
  console.log("[BlockchainAPI] Total supply: " + tokenCount);

  const indexes: number[] = [];
  for (let i = tokenCount - 1; i >= 0 && i > tokenCount - 1 - maxToDisplay; i--) {
    indexes.push(i);
  }

  const tokenIds = await Promise.all(indexes.map((index) => contract.tokenByIndex(index)));

  return tokenIds.map((tokenId) => tokenId.toString());
}

/** Token IDs held by an account, newest first. Batched as above. */
export async function fetchOwnedTokenIds(account: string): Promise<string[]> {
  const contract = await getContract();

  const tokenCount = Number(await contract.balanceOf(account));
  console.log("[BlockchainAPI] Tokens owned: " + tokenCount);

  const indexes = Array.from({ length: tokenCount }, (_unused, index) => index);
  const tokenIds = await Promise.all(indexes.map((index) => contract.tokenOfOwnerByIndex(account, index)));

  // Owner index order isn't token ID order -- sort descending like the recent list.
  return tokenIds
    .sort((a, b) => (b > a ? 1 : b < a ? -1 : 0))
    .map((tokenId) => tokenId.toString());
}

export async function setMintPriceOnContract(newMintPrice: string): Promise<void> {
  const wei = ethers.parseEther(newMintPrice);

  const contractWithSigner = await getContractWithSigner();
  await contractWithSigner.setMintPrice(wei);
}

export async function makePaymentFromContract(amount: string): Promise<void> {
  const wei = ethers.parseEther(amount);
  console.log("Making payment wei: " + wei.toString());

  const contractWithSigner = await getContractWithSigner();
  await contractWithSigner.payOwner(wei);
}

export async function fetchContractBalance(): Promise<string> {
  const balance = await getReadProvider().getBalance(DynaStripesContractAddress);
  return ethers.formatEther(balance);
}
